import React from 'react';
import Tooltip from '../Tooltip/Tooltip';
import scheduleConflictIcon from '../../assets/images/event-busy.svg';
import overlappingIcon from '../../assets/images/join-left.svg';
import unavailableIcon from '../../assets/images/beach-access.svg';

/**
 * Returns whether the given conflict-date set has at least one date that is
 * NOT present in any of the higher-precedence sets. Used by `ConflictBadgeRow`
 * to decide whether a badge should render: a conflict type's badge only shows
 * when at least one of its dates would actually render in that badge's color
 * after classifyDateConflict precedence is applied.
 *
 * Example: a date that's in both `unavailableDates` and `overlappingDates`
 * paints the chip red (Schedule conflict precedence). Calling this as
 * hasErrorDateWithoutHigherPrecedence(unavailableDates, [overlappingDates, internalOverlapDates])
 * returns true only if the unavailable set has at least one date that isn't
 * shadowed by either higher-precedence set.
 *
 * @param {Set<string>} [set] The conflict set whose visibility is being checked.
 * @param {Array<Set<string> | undefined>} higherPrecedenceSets The other
 *   conflict sets that take precedence over `set` and shadow any shared date.
 *   Individual entries may be `undefined` (treated as empty).
 * @returns {boolean} `true` if at least one date in `set` is absent from every
 *   provided higher-precedence set; `false` if `set` is empty/missing or every
 *   one of its dates is shadowed.
 */
const hasErrorDateWithoutHigherPrecedence = (set, higherPrecedenceSets) => {
	if (!set || set.size === 0) {
		return false;
	}
	const dates = Array.from(set);
	for (let i = 0; i < dates.length; i += 1) {
		const date = dates[i];
		let foundInHigherPrecedence = false;
		for (let j = 0; j < higherPrecedenceSets.length; j += 1) {
			const higherSet = higherPrecedenceSets[j];
			if (higherSet && higherSet.has(date)) {
				foundInHigherPrecedence = true;
				break;
			}
		}
		if (!foundInHigherPrecedence) {
			return true;
		}
	}
	return false;
};

const BADGES = [
	{
		key: 'invalid-date',
		label: 'Schedule conflict',
		variantClass: 'conflict-badge--schedule-conflict',
		icon: scheduleConflictIcon,
		tooltip:
			'Selected dates cannot be scheduled for the selected times. These dates must be removed before continuing.',
		hasItems: (errors) => {
			return errors && !errors.bulkAssignError && !errors.dates && errors.invalidDate;
		}
	},
	{
		key: 'schedule-conflict',
		label: 'Schedule conflict',
		variantClass: 'conflict-badge--schedule-conflict',
		icon: scheduleConflictIcon,
		tooltip:
			"Selected dates conflict with the professional's schedule and/or cannot be scheduled for the selected times. These dates must be removed before continuing.",
		hasItems: (errors) => {
			return (
				errors && errors.bulkAssignError && !errors.dates && (errors.invalidDate || errors.overlappingDates?.size > 0)
			);
		}
	},
	{
		key: 'overlapping',
		label: 'Overlapping shift',
		variantClass: 'conflict-badge--overlapping',
		icon: overlappingIcon,
		tooltip:
			'The requested dates overlap and cannot be scheduled. Remove any shift requests that overlap before continuing.',
		hasItems: (errors) => {
			if (!errors) {
				return false;
			}
			return hasErrorDateWithoutHigherPrecedence(errors.internalOverlapDates, [errors.overlappingDates]);
		}
	},
	{
		key: 'unavailable',
		label: 'Unavailable',
		variantClass: 'conflict-badge--unavailable',
		icon: unavailableIcon,
		tooltip: 'The professional is set to unavailable on the selected dates.',
		hasItems: (errors) => {
			if (!errors) {
				return false;
			}
			return hasErrorDateWithoutHigherPrecedence(errors.unavailableDates, [
				errors.overlappingDates,
				errors.internalOverlapDates
			]);
		}
	}
];

/**
 * Renders a row of pill badges describing the conflict types present in the
 * selected-dates set. One badge appears per non-empty Set on the `errors`
 * object: Schedule Conflict (`overlappingDates`), Unavailable
 * (`unavailableDates`), and Overlapping Shifts (`internalOverlapDates`).
 *
 * Each badge is wrapped in the project's `Tooltip` so hovering shows the
 * full explanation. The component returns `null` when no conflict Sets are
 * populated, leaving no spacing artifacts in the parent layout.
 *
 * @param {{
 *   errors: {
 *     overlappingDates?: Set<string>,
 *     unavailableDates?: Set<string>,
 *     internalOverlapDates?: Set<string>
 *   }
 * }} props
 * @returns {(React.ReactElement | null)}
 */
const ConflictBadgeRow = ({ errors }) => {
	const visibleBadges = BADGES.filter((badge) => badge.hasItems(errors));
	if (visibleBadges.length === 0) {
		return null;
	}
	return (
		<div className="conflict-badge-row">
			{visibleBadges.map((badge) => (
				<Tooltip
					key={badge.key}
					label={badge.tooltip}
					placement="right"
					customClass="conflict-badge-tooltip"
					className={`conflict-badge ${badge.variantClass}`}
				>
					{badge.icon && <img src={badge.icon} alt={badge.label} />}
					{badge.label}
				</Tooltip>
			))}
		</div>
	);
};

export default ConflictBadgeRow;
